import {
  FEEDBACK_LIMIT_VALUES,
  SAFE_RECORDED_PATH,
  diagnosticsWithinLimit,
  type FeedbackDiagnostics,
  type RecordedError,
  type RecordedRequest,
  type VisitedPage,
} from '@myrewards/contracts';
import { redactMessage, stripQuery } from '@myrewards/domain';

// APP-104 §4: small in-memory rings of what just happened, taken at the click of "Send feedback"
// and sent with the report. They live only in this tab's memory, are never written to storage,
// and are never part of the telemetry payload (APP-097, APP-098).

export type BrowserErrorType = RecordedError['type'];

const L = FEEDBACK_LIMIT_VALUES;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** The feedback calls themselves are not evidence about what went wrong. */
const FEEDBACK_PATH = /^\/api\/v1\/feedback(?:\/|$)/;

let requests: RecordedRequest[] = [];
let errors: RecordedError[] = [];
let pages: VisitedPage[] = [];

function push<T>(ring: T[], item: T, max: number): T[] {
  ring.push(item);
  return ring.length > max ? ring.slice(ring.length - max) : ring;
}

/**
 * A path as recorded: no query or fragment, dot segments and encodings settled by the URL parser,
 * and only one the report's contract accepts (else null).
 */
export function recordable(path: string): string | null {
  const clean = stripQuery(path);
  // Checked before parsing: `//host` and `/\host` would be read as another site's address.
  // eslint-disable-next-line no-control-regex -- control characters are exactly what is refused
  if (!/^\/(?:[^/\\]|$)/.test(clean) || /[\u0000-\u001f\u007f\\]/.test(clean)) return null;
  try {
    const settled = new URL(clean, 'https://site.invalid').pathname.slice(0, 300);
    return SAFE_RECORDED_PATH.test(settled) ? settled : null;
  } catch {
    return null;
  }
}

const now = (): string => new Date().toISOString();

export function recordRequest(r: {
  method: string;
  path: string;
  route?: string;
  status: number | null;
  ms: number;
  requestId: string;
}): void {
  try {
    const path = recordable(r.path);
    if (path === null || FEEDBACK_PATH.test(path)) return;
    requests = push(
      requests,
      {
        at: now(),
        method: r.method.toUpperCase().slice(0, 10),
        path,
        routeId: r.route === undefined ? null : stripQuery(r.route).slice(0, 300),
        status: r.status === null ? 0 : Math.min(599, Math.max(0, Math.trunc(r.status))),
        durationMs: Math.min(3_600_000, Math.max(0, Math.round(r.ms))),
        requestId: UUID.test(r.requestId) ? r.requestId : null,
      },
      L.ringRequests,
    );
  } catch {
    // Diagnostics never get in the way of a request.
  }
}

/** Own-origin stack frames only, with the query string cut off the script address. */
function framesOf(stack: string): string[] {
  const origin = window.location.origin;
  const out: string[] = [];
  for (const line of stack.split('\n')) {
    const url = /https?:\/\/[^\s)]+/.exec(line)?.[0];
    if (url === undefined || !url.startsWith(`${origin}/`)) continue;
    const clean = url.replace(/\?[^:\s)]*/, '');
    out.push(line.replace(url, clean).trim().slice(0, 300));
    if (out.length === L.stackFrames) break;
  }
  return out;
}

function describeError(error: unknown): { name: string; message: string; frames: string[] } {
  try {
    if (typeof error === 'string')
      return { name: 'Error', message: redactMessage(error), frames: [] };
    if (typeof error === 'object' && error !== null) {
      const e = error as { name?: unknown; message?: unknown; stack?: unknown };
      return {
        name: redactMessage(typeof e.name === 'string' ? e.name : 'Error').slice(0, 100),
        message: redactMessage(typeof e.message === 'string' ? e.message : ''),
        frames: typeof e.stack === 'string' ? framesOf(e.stack) : [],
      };
    }
  } catch {
    // A rejected value can be a Proxy with throwing getters.
  }
  return { name: 'Error', message: '', frames: [] };
}

export function recordError(e: {
  type: BrowserErrorType;
  area: string;
  error: unknown;
  requestId?: string;
}): void {
  try {
    errors = push(
      errors,
      {
        at: now(),
        type: e.type,
        area: e.area.slice(0, 100),
        ...describeError(e.error),
        requestId: e.requestId !== undefined && UUID.test(e.requestId) ? e.requestId : null,
      },
      L.ringErrors,
    );
  } catch {
    // Same: never get in the way.
  }
}

export function recordPage(path: string, routeId: string | null): void {
  try {
    const clean = recordable(path);
    if (clean === null || pages.at(-1)?.path === clean) return;
    pages = push(
      pages,
      {
        at: now(),
        path: clean,
        routeId: routeId === null ? null : stripQuery(routeId).slice(0, 300),
      },
      L.ringPages,
    );
  } catch {
    // Same.
  }
}

/** The rings as they are, before any trimming to fit. */
export function untrimmed(): FeedbackDiagnostics {
  return { requests: [...requests], errors: [...errors], pages: [...pages] };
}

const size = (v: unknown): number => new TextEncoder().encode(JSON.stringify(v)).length;

/** Taken at the click. Newest last; over 65,536 bytes, the oldest entries of the heaviest ring go first. */
export function snapshot(): FeedbackDiagnostics {
  const d = untrimmed();
  while (!diagnosticsWithinLimit(d)) {
    const heaviest = (['requests', 'errors', 'pages'] as const)
      .filter((k) => d[k].length > 0)
      .sort((a, b) => size(d[b]) - size(d[a]))[0];
    if (heaviest === undefined) break;
    d[heaviest].shift();
  }
  return d;
}

/** Sign-out: nothing of this person's session stays in the tab. */
export function clearDiagnostics(): void {
  requests = [];
  errors = [];
  pages = [];
}
